// https://github.com/NightCafeStudio/react-render-if-visible
// copying from github so that it can be extended
import ErrorBoundary from '@/Components/ErrorBoundary';
import React, { useEffect, useMemo, useRef, useState } from 'react';

type Props = {
  /**
   * Whether the element should be visible initially or not.
   * Useful e.g. for always setting the first N items to visible.
   * Default: false
   */
  initialVisible?: boolean;
  /** An estimate of the element's height */
  defaultHeight?: number;
  /** How far outside the viewport in percentage should elements be considered visible?   */
  visibleOffset?: number;
  /** Should the element stay rendered after it becomes visible? */
  stayRendered?: boolean;
  root?: HTMLElement | null;
  /** E.g. 'span', 'tbody'. Default = 'div' */
  /** Or JSX.element without any children */
  rootElement?: string | Omit<React.ReactElement, 'children'>;
  rootElementClass?: string;
  onChange?: (isVisible: boolean) => void;
  fallback?: React.ReactNode;
  children: React.ReactNode;
};

const RenderIfVisible = ({
  initialVisible = false,
  defaultHeight = 300,
  visibleOffset = 75,
  stayRendered = false,
  root = null,
  rootElement = 'div',
  rootElementClass = '',
  onChange,
  fallback,
  children,
}: Props) => {
  const [isVisible, setIsVisible] = useState<boolean>(initialVisible);
  const wasVisible = useRef<boolean>(initialVisible);
  const placeholderHeight = useRef<number>(defaultHeight);
  const intersectionRef = useRef<HTMLDivElement>(null);
  const onChangeRef = useRef(onChange);

  // update the onChange callback
  useEffect(() => {
    onChangeRef.current = onChange;

    return () => {
      onChangeRef.current = undefined;
    };
  }, [onChange]);

  // Set visibility with intersection observer
  useEffect(() => {
    let idleCallbackId: number | null = null;

    if (intersectionRef.current) {
      const localRef = intersectionRef.current;
      const observer = new IntersectionObserver(
        (entries) => {
          // Before switching off `isVisible`, set the height of the placeholder
          if (!entries[0].isIntersecting) placeholderHeight.current = localRef!.offsetHeight;

          if (typeof window !== 'undefined' && window.requestIdleCallback)
            idleCallbackId = window.requestIdleCallback(
              () => {
                setIsVisible(entries[0].isIntersecting);
              },
              {
                timeout: 1000,
              }
            );
          else setIsVisible(entries[0].isIntersecting);

          onChangeRef?.current?.(entries[0].isIntersecting);
        },
        { root, rootMargin: `${visibleOffset}% 0px ${visibleOffset}% 0px` }
      );

      observer.observe(localRef);
      return () => {
        if (localRef) {
          observer.unobserve(localRef);
        }
        if (idleCallbackId !== null && typeof window !== 'undefined' && window.cancelIdleCallback) {
          window.cancelIdleCallback(idleCallbackId);
        }
      };
    }
    return () => {};
  }, []);

  useEffect(() => {
    if (isVisible) wasVisible.current = true;

    return () => {
      wasVisible.current = false;
    };
  }, [isVisible]);

  const rootClasses = useMemo(() => `renderIfVisible ${rootElementClass}`, [rootElementClass]);

  if (typeof rootElement === 'string')
    return React.createElement(rootElement, {
      // biome-ignore lint/correctness/noChildrenProp: Custom component
      children:
        isVisible || (stayRendered && wasVisible.current) ? (
          <ErrorBoundary onError={{ render: children }}>{children}</ErrorBoundary>
        ) : (
          fallback
        ),
      ref: intersectionRef,
      className: rootClasses,
    });

  return React.cloneElement(rootElement, {
    // @ts-ignore
    children:
      isVisible || (stayRendered && wasVisible.current) ? (
        <ErrorBoundary onError={{ render: children }}>{children}</ErrorBoundary>
      ) : (
        fallback
      ),
    ref: intersectionRef,
    className: `${rootClasses} ${(rootElement as React.ReactElement<HTMLDivElement>).props?.className || ''}`,
  });
};

export type { Props as RenderIfVisibleProps };

export default React.memo(RenderIfVisible);
